import React from 'react';
import Icon from '../../../components/AppIcon';

const FormProgressIndicator = ({ 
  currentStep, 
  steps, 
  onStepClick,
  className = '' 
}) => {
  const getStepStatus = (stepIndex) => {
    if (stepIndex < currentStep) return 'completed';
    if (stepIndex === currentStep) return 'current';
    return 'upcoming';
  };

  const getStepClasses = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success text-success-foreground border-success';
      case 'current':
        return 'bg-primary text-primary-foreground border-primary';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getConnectorClasses = (stepIndex) => {
    const isCompleted = stepIndex < currentStep;
    return isCompleted ? 'bg-success' : 'bg-border';
  };

  return (
    <div className={`bg-card rounded-lg border border-border p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-foreground mb-6">Upload Progress</h3>
      
      {/* Desktop Progress */}
      <div className="hidden md:flex items-center justify-between">
        {steps.map((step, index) => (
          <React.Fragment key={index}>
            <div className="flex flex-col items-center flex-1">
              <button
                onClick={() => onStepClick(index)}
                disabled={index > currentStep}
                className={`
                  w-10 h-10 rounded-full border-2 flex items-center justify-center text-sm font-medium transition-smooth
                  ${getStepClasses(getStepStatus(index))}
                  ${index <= currentStep ? 'cursor-pointer hover:scale-105' : 'cursor-not-allowed opacity-50'}
                `}
              >
                {getStepStatus(index) === 'completed' ? (
                  <Icon name="Check" size={16} />
                ) : (
                  <span>{index + 1}</span>
                )}
              </button>
              <div className="mt-3 text-center">
                <p className={`text-sm font-medium ${
                  getStepStatus(index) === 'current' ? 'text-foreground' : 'text-muted-foreground'
                }`}>
                  {step.title}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {step.description}
                </p>
              </div>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`flex-1 h-0.5 mx-4 transition-smooth ${getConnectorClasses(index)}`}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Mobile Progress */}
      <div className="md:hidden space-y-4">
        {steps.map((step, index) => {
          const status = getStepStatus(index);
          return (
            <div key={index} className="flex items-start space-x-3">
              <div className="flex flex-col items-center">
                <button
                  onClick={() => onStepClick(index)}
                  disabled={index > currentStep}
                  className={`
                    w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-medium transition-smooth
                    ${getStepClasses(status)}
                    ${index <= currentStep ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}
                  `}
                >
                  {status === 'completed' ? (
                    <Icon name="Check" size={14} />
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </button>
                {index < steps.length - 1 && (
                  <div
                    className={`w-0.5 h-8 mt-2 transition-smooth ${getConnectorClasses(index)}`}
                  />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${
                  status === 'current' ? 'text-foreground' : 'text-muted-foreground'
                }`}>
                  {step.title}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {step.description}
                </p>
                {status === 'current' && (
                  <div className="mt-2 w-full bg-muted rounded-full h-1">
                    <div className="bg-primary h-1 rounded-full w-1/3 transition-smooth"></div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Step Summary */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Step {currentStep + 1} of {steps.length}
          </span>
          <span className="text-foreground font-medium">
            {Math.round(((currentStep + 1) / steps.length) * 100)}% Complete
          </span>
        </div>
        <div className="mt-2 w-full bg-muted rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full transition-smooth"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default FormProgressIndicator;